import React, { useRef, useState, useEffect } from "react";

import {
  Button,
  TextField,
  IconButton,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Chip,
  LinearProgress,
  Switch,
} from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CloseIcon from "@material-ui/icons/Close";
import DescriptionIcon from "@material-ui/icons/Description";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Slide from "@material-ui/core/Slide";
import { parseJSONfromText } from "../../author/hooks/useImportPlans";
import { firestore } from "../../../../database/firebase";
import DB_PATH from "../../../../database/DB_PATH";
import { Habit, inferPracticeType } from "../../../../types/Habit";
import { Community } from "../../../../types/Community";
import {
  generateSingleHabit,
  UploadedFile as OnboardingUploadedFile,
} from "../../onboarding/OnboardingAPI";

type Message = {
  role: "user" | "assistant";
  content: string;
};

const renderSlide = (props: any) => <Slide direction="up" {...props} />;

const ImportHabits = ({ communityId }) => {
  const [show, setShow] = useState(false);
  const [data, setData] = useState<any>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedSource, setUploadedSource] =
    useState<OnboardingUploadedFile | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const [community, setCommunity] = useState<Community | null>(null);
  const [useExternalGPT, setUseExternalGPT] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchCommunity = async () => {
      if (!communityId) return;
      try {
        const doc = await firestore
          .collection(DB_PATH.COMMUNITY)
          .doc(communityId)
          .get();
        if (doc.exists) {
          setCommunity({ id: doc.id, ...doc.data() } as Community);
        }
      } catch (error) {
        console.error("Error fetching community:", error);
      }
    };
    fetchCommunity();
  }, [communityId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const deriveHabitNameFromFile = (fileName: string): string => {
    const withoutExtension = fileName.replace(/\.[^/.]+$/, "").trim();
    const normalized = withoutExtension.replace(/[_-]+/g, " ").trim();
    return normalized || "Habit";
  };

  const toOnboardingUploadedFile = async (
    file: File
  ): Promise<OnboardingUploadedFile> => {
    const lowerName = file.name.toLowerCase();
    const isTextFile =
      file.type === "text/plain" || lowerName.endsWith(".txt") || lowerName.endsWith(".md");

    if (isTextFile) {
      const text = await file.text();
      return {
        name: file.name,
        mimeType: "text/plain",
        text: text.slice(0, 40000),
      };
    }

    return {
      name: file.name,
      mimeType: file.type || "application/octet-stream",
      file,
    };
  };

  const generateHabitFromSource = async (
    source: OnboardingUploadedFile,
    options?: {
      habitName?: string;
      additionalGuidance?: string;
    }
  ) => {
    const inferredHabitName =
      options?.habitName?.trim() || deriveHabitNameFromFile(source.name);

    return generateSingleHabit({
      sources: { uploadedFiles: [source] },
      habitName: inferredHabitName,
      authorName: community?.author?.fullName || undefined,
      communityName: community?.title || undefined,
      additionalGuidance: options?.additionalGuidance?.trim() || undefined,
    });
  };

  const inferRequestedTitle = (value: string): string | undefined => {
    const normalized = value.replace(/\s+/g, " ").trim();
    if (!normalized) return undefined;
    if (normalized.length > 80) return undefined;
    if (/[\n.!?]/.test(normalized)) return undefined;
    return normalized;
  };

  // Handle file upload
  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (useExternalGPT) return;
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadedFile(file);
    setUploadProgress("Reading file...");

    try {
      const source = await toOnboardingUploadedFile(file);
      setUploadedSource(source);

      setUploadProgress("Generating one habit from this file...");
      const generatedHabit = await generateHabitFromSource(source);
      setData(generatedHabit);

      const totalGeneratedActions = generatedHabit.actions?.length || 0;
      const totalGeneratedSubactions =
        generatedHabit.actions?.reduce(
          (acc, action) => acc + (action.subactions?.length || 0),
          0
        ) || 0;

      setMessages([
        {
          role: "assistant",
          content: `Created one habit from "${file.name}".\n\nHabit: ${generatedHabit.habitName}\nActions: ${totalGeneratedActions}\nSubactions: ${totalGeneratedSubactions}\n\nClick "Import Habit" to save it, or type guidance to regenerate from the same file.`,
        },
      ]);
    } catch (error) {
      console.error("Error uploading file:", error);
      const errorMsg =
        error instanceof Error ? error.message : "Unknown error";
      setMessages([
        {
          role: "assistant",
          content: `Error generating a habit from this file: ${errorMsg}`,
        },
      ]);
      setUploadedFile(null);
      setUploadedSource(null);
    } finally {
      setIsUploading(false);
      setUploadProgress("");
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeFile = async () => {
    setUploadedFile(null);
    setUploadedSource(null);
  };

  const sendMessage = async () => {
    if (useExternalGPT) {
      handleManualJSONImport();
      return;
    }
    if (isGenerating) return;

    const trimmedInput = inputValue.trim();
    if (!trimmedInput) return;

    const userMessage: Message | null = trimmedInput
      ? { role: "user", content: trimmedInput }
      : null;
    const newMessages = userMessage ? [...messages, userMessage] : messages;

    if (userMessage) setMessages(newMessages);
    setInputValue("");
    setIsGenerating(true);

    try {
      if (uploadedSource) {
        const generatedHabit = await generateHabitFromSource(uploadedSource, {
          habitName: inferRequestedTitle(trimmedInput),
          additionalGuidance: trimmedInput || undefined,
        });

        const totalGeneratedActions = generatedHabit.actions?.length || 0;
        const totalGeneratedSubactions =
          generatedHabit.actions?.reduce(
            (acc, action) => acc + (action.subactions?.length || 0),
            0
          ) || 0;

        setData(generatedHabit);
        setMessages([
          ...newMessages,
          {
            role: "assistant",
            content: `Regenerated habit from "${uploadedSource.name}".\n\nHabit: ${generatedHabit.habitName}\nActions: ${totalGeneratedActions}\nSubactions: ${totalGeneratedSubactions}`,
          },
        ]);
        return;
      }

      if (!trimmedInput) return;

      const generatedHabit = await generateSingleHabit({
        sources: { pastedContent: trimmedInput },
        habitName: inferRequestedTitle(trimmedInput),
        authorName: community?.author?.fullName || undefined,
        communityName: community?.title || undefined,
        additionalGuidance: trimmedInput,
      });

      const totalGeneratedActions = generatedHabit.actions?.length || 0;
      const totalGeneratedSubactions =
        generatedHabit.actions?.reduce(
          (acc, action) => acc + (action.subactions?.length || 0),
          0
        ) || 0;

      setData(generatedHabit);
      setMessages([
        ...newMessages,
        {
          role: "assistant",
          content: `Generated one habit.\n\nHabit: ${generatedHabit.habitName}\nActions: ${totalGeneratedActions}\nSubactions: ${totalGeneratedSubactions}`,
        },
      ]);
    } catch (error) {
      console.error("Error generating habit:", error);
      const errorMsg =
        error instanceof Error ? error.message : "Unknown error";
      setMessages([
        ...newMessages,
        {
          role: "assistant",
          content: uploadedSource
            ? `Error generating habit from the uploaded file: ${errorMsg}`
            : `Error: ${errorMsg}`,
        },
      ]);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleManualJSONImport = () => {
    if (!inputValue.trim()) return;
    try {
      const parsed = parseJSONfromText(inputValue);
      const first = Array.isArray(parsed) ? parsed[0] : parsed;
      setData(first);
      setMessages([
        {
          role: "assistant",
          content: "JSON imported from external GPT link. Habit preview updated.",
        },
      ]);
    } catch (error) {
      console.error("Failed to parse JSON from manual input:", error);
      setMessages([
        {
          role: "assistant",
          content:
            "Could not parse the JSON you pasted. Please ensure it is valid JSON (array or object).",
        },
      ]);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const parseDataToHabits = () => {
    if (!data) {
      console.log("No Data to parse");
      return;
    }

    const habit: Habit = {
      id: "",
      habitName: data.habitName || "",
      habitDescription: data.habitDescription || "",
      creationDate: new Date().toISOString(),
      targetFrequency: data.targetFrequency || "daily",
      levels: data.levels || [],
      learningPlansIds: data.learningPlansIds || [],
      backgroundImageURL: data.backgroundImageURL || "",
      practiceType: data.practiceType ?? inferPracticeType(data.actions),
      authorsIds:
        Array.isArray(data.authorsIds) && data.authorsIds.length
          ? data.authorsIds
          : community?.author?.id
            ? [community.author.id]
            : [],
      learningPlans: data.learningPlans || [],
      status: typeof data.status === "boolean" ? data.status : true,
      actions: Array.isArray(data.actions) ? data.actions : [],
      communityId,
      tracking: Array.isArray(data.tracking) ? data.tracking : [],
    };

    return habit;
  };

  const uploadHabit = async () => {
    console.log("Uploading Habit", data);

    if (!data) return;

    const parsedData = parseDataToHabits();
    if (!parsedData) return;

    try {
      const now = new Date().toISOString();
      const habitRef = firestore.collection(DB_PATH.HABIT).doc();
      const habitID = habitRef.id;
      const actionCollection = firestore.collection(DB_PATH.HABIT_ACTION);
      const subactionCollection = firestore.collection(DB_PATH.HABIT_SUBACTION);

      const normalizedActions = (parsedData.actions || []).map((action: any) => {
        const actionID =
          typeof action?.id === "string" && action.id.trim()
            ? action.id.trim()
            : actionCollection.doc().id;
        const normalizedSubactions = (Array.isArray(action?.subactions)
          ? action.subactions
          : []
        ).map((subaction: any) => ({
          ...subaction,
          id:
            typeof subaction?.id === "string" && subaction.id.trim()
              ? subaction.id.trim()
              : subactionCollection.doc().id,
          actionId: actionID,
          habitId: habitID,
          communityId,
          createdAt: subaction?.createdAt || now,
          updatedAt: subaction?.updatedAt || now,
        }));

        return {
          ...action,
          id: actionID,
          habitId: habitID,
          communityId,
          createdAt: action?.createdAt || now,
          updatedAt: action?.updatedAt || now,
          subactions: normalizedSubactions,
        };
      });

      const habit: Habit = {
        ...parsedData,
        id: habitID,
        creationDate: parsedData.creationDate || now,
        actions: normalizedActions,
      };

      await habitRef.set(habit);
      console.log("HabitAdded", habit);

      for (const action of normalizedActions) {
        const actionRef = actionCollection.doc(action.id);
        const actionDoc = await actionRef.get();

        if (!actionDoc.exists) {
          await actionRef.set(action);
          console.log("ActionAdded", action);
        }

        await firestore.collection(DB_PATH.HABIT_ACTION_LINK).add({
          habitID,
          actionID: action.id,
        });

        for (const subaction of action.subactions || []) {
          const subactionRef = subactionCollection.doc(subaction.id);
          const subactionDoc = await subactionRef.get();

          if (!subactionDoc.exists) {
            await subactionRef.set(subaction);
            console.log("SubactionAdded", subaction);
          }

          await firestore.collection(DB_PATH.ACTION_SUBACTION_LINK).add({
            actionID: action.id,
            subactionID: subaction.id,
          });
        }
      }

      console.log("Habits uploaded successfully!");
      handleClose();
    } catch (error) {
      console.error("Error uploading habits: ", error);
    }
  };

  const handleClose = async () => {
    await removeFile();

    setShow(false);
    setMessages([]);
    setData(null);
    setInputValue("");
  };

  const renderMessage = (message: Message, index: number) => {
    const isUser = message.role === "user";
    return (
      <Box
        key={index}
        display="flex"
        justifyContent={isUser ? "flex-end" : "flex-start"}
        mb={2}
      >
        <Paper
          elevation={1}
          style={{
            padding: "12px 16px",
            maxWidth: "80%",
            backgroundColor: isUser ? "#1976d2" : "#f5f5f5",
            color: isUser ? "#fff" : "#333",
            borderRadius: isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
          }}
        >
          <Typography
            variant="body2"
            style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          >
            {message.content}
          </Typography>
        </Paper>
      </Box>
    );
  };

  // Calculate total actions and subactions count
  const totalActions = data?.actions?.length || 0;
  const totalSubactions =
    data?.actions?.reduce(
      (acc: number, action: any) => acc + (action.subactions?.length || 0),
      0
    ) || 0;

  return (
    <div>
      <Button
        style={{ marginTop: 8 }}
        color="primary"
        variant="outlined"
        onClick={() => setShow(true)}
      >
        Import Habits with AI
      </Button>

      <Dialog
        open={show}
        onClose={handleClose}
        TransitionComponent={renderSlide}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: { height: "80vh", display: "flex", flexDirection: "column" },
        }}
      >
        <DialogTitle>Create Habit with AI</DialogTitle>

        <DialogContent
          style={{ flex: 1, display: "flex", flexDirection: "column" }}
        >
          {/* Mode toggle */}
          <Box
            mb={2}
            p={2}
            style={{
              backgroundColor: "#eef2ff",
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <Box>
              <Typography variant="subtitle2" color="primary">
                Habit creation mode
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Toggle to paste JSON from the external GPT link instead of uploading here.
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Typography variant="caption" color="textSecondary">
                Upload & Chat
              </Typography>
              <Switch
                color="primary"
                checked={useExternalGPT}
                onChange={(e) => setUseExternalGPT(e.target.checked)}
              />
              <Typography variant="caption" color="textSecondary">
                Paste JSON
              </Typography>
            </Box>
          </Box>

          {/* External GPT helper */}
          {useExternalGPT && (
            <Box
              p={2}
              mb={2}
              style={{ backgroundColor: "#f0f7ff", borderRadius: 8 }}
            >
              <Typography variant="body1" gutterBottom>
                Generate the habit in ChatGPT, then paste the JSON here.
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                href="https://chatgpt.com/g/g-69246795899481919cd26f56f64c9647-habit-ai-app-designer"
                target="_blank"
                rel="noreferrer"
                style={{ marginBottom: 8 }}
              >
                Open Habit AI App Designer
              </Button>
              <Typography variant="body2" color="textSecondary">
                After you get the JSON response, paste it below and hit Send to import.
              </Typography>
            </Box>
          )}

          {/* File Upload Area */}
          {!useExternalGPT && !uploadedFile && messages.length === 0 && !isUploading && (
            <Box
              p={3}
              mb={2}
              style={{
                backgroundColor: "#f0f4ff",
                borderRadius: 8,
                border: "2px dashed #1976d2",
                textAlign: "center",
              }}
            >
              <DescriptionIcon
                style={{ fontSize: 48, color: "#1976d2", marginBottom: 8 }}
              />
              <Typography variant="body1" gutterBottom>
                Upload a book or document as reference (optional)
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Supported formats: PDF, TXT, MD, DOCX
              </Typography>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".pdf,.txt,.md,.docx"
                style={{ display: "none" }}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<AttachFileIcon />}
                onClick={() => fileInputRef.current?.click()}
                style={{ marginTop: 8 }}
              >
                Select file
              </Button>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginTop: 16 }}
              >
                Or simply describe the habit you want to create
              </Typography>
            </Box>
          )}

          {/* Upload Progress */}
          {isUploading && (
            <Box p={3} mb={2} textAlign="center">
              <CircularProgress size={40} />
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginTop: 16 }}
              >
                {uploadProgress || "Processing..."}
              </Typography>
              <LinearProgress style={{ marginTop: 16 }} />
            </Box>
          )}

          {/* Uploaded File Indicator */}
          {!useExternalGPT && uploadedFile && !isUploading && (
            <Box mb={2}>
              <Chip
                icon={<DescriptionIcon />}
                label={`${uploadedFile.name} (analyzed source)`}
                onDelete={removeFile}
                deleteIcon={<CloseIcon />}
                color="primary"
                variant="outlined"
              />
            </Box>
          )}

          {/* Chat Messages Area */}
          <Box
            flex={1}
            overflow="auto"
            p={2}
            style={{ backgroundColor: "#fafafa", borderRadius: 8 }}
          >
            {!useExternalGPT && messages.length === 0 && !uploadedFile && !isUploading && (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="textSecondary">
                  Describe the habit you want to create.
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ marginTop: 8 }}
                >
                  Example: "Create a morning workout habit by David Goggins with
                  cardio, strength, and stretching actions"
                </Typography>
              </Box>
            )}

            {messages.map((msg, idx) => renderMessage(msg, idx))}

            {isGenerating && (
              <Box
                display="flex"
                alignItems="center"
                ml={1}
                style={{ gap: 8 }}
              >
                <CircularProgress size={20} />
                <Typography variant="body2" color="textSecondary">
                  Generating...
                </Typography>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          {/* Habit Preview */}
          {data && (
            <Box
              mt={2}
              p={2}
              style={{ backgroundColor: "#e8f5e9", borderRadius: 8 }}
            >
              <Typography variant="subtitle2" color="primary">
                Habit ready to import:
              </Typography>
              <Typography variant="body2">
                <strong>{data.habitName}</strong> - {totalActions} actions
                {totalSubactions > 0 && `, ${totalSubactions} subactions`}
              </Typography>
              {data.targetFrequency && (
                <Typography variant="caption" color="textSecondary">
                  Frequency: {data.targetFrequency}
                </Typography>
              )}
            </Box>
          )}

          {/* Input Area */}
          <Box display="flex" alignItems="center" mt={2} style={{ gap: 8 }}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".pdf,.txt,.md,.docx"
              style={{ display: "none" }}
            />
            {!useExternalGPT && messages.length > 0 && !uploadedFile && (
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading || isGenerating}
                size="small"
              >
                <AttachFileIcon />
              </IconButton>
            )}
            <TextField
              fullWidth
              variant="outlined"
              placeholder={
                useExternalGPT
                  ? "Paste JSON from the GPT link here..."
                  : "Type your message..."
              }
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={isGenerating || isUploading}
              size="small"
              multiline
              maxRows={useExternalGPT ? 8 : 3}
            />
            <IconButton
              color="primary"
              onClick={sendMessage}
              disabled={!inputValue.trim() || isGenerating || isUploading}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="default">
            Cancel
          </Button>
          <Button
            onClick={uploadHabit}
            color="primary"
            variant="contained"
            disabled={!data}
          >
            Import Habit
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ImportHabits;
